/**
 * ADR-0095: Watermark binding subsection (Config side).
 *
 * Renders only when the persist's write_mode is keyed (append / merge /
 * merge_history). Editable at ALL times (unlike ADR-0094's policy/key
 * controls which are frozen post-creation) — the binding lives on the
 * editable operation spec, not on the frozen PDO config.
 *
 * Reads/writes `spec_persist.watermark` via setNodeData. Server-side
 * autosave picks up the change on the next operation PATCH and the
 * sync hook (binding_added / binding_changed / binding_removed) handles
 * the corresponding state-row mutation.
 */

import { useMemo } from "react";

type OperationVariableType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";
type OperationVariableDef = {
  name: string;
  type: OperationVariableType;
  value: string | number | boolean | null;
};

/** Variable types eligible for watermark binding (orderable; bool excluded). */
const ORDERABLE_TYPES = new Set<OperationVariableType>(["string", "int", "decimal", "date", "datetime"]);

type StrategyKind = "max_of_column" | "last_run_time" | "formula";
type BufferUnit = "minutes" | "hours" | "days";

export type WatermarkStrategy =
  | { kind: "max_of_column"; column: string; buffer_value?: number; buffer_unit?: BufferUnit }
  | { kind: "last_run_time"; buffer_value?: number; buffer_unit?: BufferUnit }
  | { kind: "formula"; expression: string };

export type WatermarkBinding = {
  variable_name: string;
  initial_load_value: string;
  strategy: WatermarkStrategy;
};

/**
 * Defaults per ADR-0095 §3.1.
 */
function defaultInitialLoadValue(type: OperationVariableType): string {
  switch (type) {
    case "date": return "1900-01-01";
    case "datetime": return "1900-01-01T00:00:00Z";
    case "int": return "0";
    case "decimal": return "0.0";
    case "string": return "";
    case "bool": return ""; // unreachable — bool is excluded from binding picker
  }
}

/**
 * Per spec §3.3 matrix: which strategies are valid for which variable types.
 */
function strategiesFor(type: OperationVariableType): StrategyKind[] {
  switch (type) {
    case "date":
    case "datetime":
      return ["max_of_column", "last_run_time", "formula"];
    case "int":
    case "decimal":
    case "string":
      return ["max_of_column", "formula"];
    case "bool":
      return [];
  }
}

function defaultStrategy(type: OperationVariableType): WatermarkStrategy {
  switch (type) {
    case "date":
    case "datetime":
    case "int":
    case "decimal":
    case "string":
      return { kind: "max_of_column", column: "" };
    case "bool":
      // Unreachable — bool is filtered out at the picker level.
      return { kind: "formula", expression: "" };
  }
}

export function WatermarkBindingSection({
  binding,
  onChange,
  operationVariables,
  upstreamColumns,
}: {
  binding: WatermarkBinding | null;
  onChange: (next: WatermarkBinding | null) => void;
  operationVariables: OperationVariableDef[];
  /** Upstream tabular_in column names — passed from the inspector for the max_of_column picker. */
  upstreamColumns: string[];
}) {
  const orderableVariables = useMemo(
    () => operationVariables.filter((v) => ORDERABLE_TYPES.has(v.type)),
    [operationVariables],
  );

  const boundVariable = binding
    ? operationVariables.find((v) => v.name === binding.variable_name)
    : null;

  const variableType = boundVariable?.type ?? null;
  const availableStrategies = variableType ? strategiesFor(variableType) : [];

  function handleVariableSelect(name: string) {
    if (!name) {
      onChange(null);
      return;
    }
    const v = orderableVariables.find((x) => x.name === name);
    if (!v) return;
    // If binding switches variable + the existing strategy is no longer valid for
    // the new variable's type, fall back to defaultStrategy.
    const currentStrategy = binding?.strategy;
    const validKinds = strategiesFor(v.type);
    const strategy: WatermarkStrategy =
      currentStrategy && validKinds.includes(currentStrategy.kind)
        ? currentStrategy
        : defaultStrategy(v.type);
    onChange({
      variable_name: v.name,
      initial_load_value: binding?.initial_load_value ?? defaultInitialLoadValue(v.type),
      strategy,
    });
  }

  function handleStrategyKindChange(kind: StrategyKind) {
    if (!binding || !variableType) return;
    if (kind === "max_of_column") {
      onChange({ ...binding, strategy: { kind: "max_of_column", column: "" } });
    } else if (kind === "last_run_time") {
      onChange({ ...binding, strategy: { kind: "last_run_time" } });
    } else {
      onChange({ ...binding, strategy: { kind: "formula", expression: "" } });
    }
  }

  // No orderable variables → empty-state per spec §3.1.
  if (orderableVariables.length === 0) {
    return (
      <div className="field">
        <label className="label">Watermark (incremental loading)</label>
        <div className="help">
          This operation has no variables suitable for a watermark. Add a typed variable
          (e.g., <code className="mono">cursor: datetime</code>) to the operation&apos;s
          variables, reference it in a provider&apos;s SQL (e.g., <code className="mono">WHERE updated_at &gt; :cursor</code>),
          then bind it here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div className="field">
        <label className="label">Watermark (incremental loading)</label>
        <select
          className="select"
          value={binding?.variable_name ?? ""}
          onChange={(e) => handleVariableSelect(e.target.value)}
        >
          <option value="">— none —</option>
          {orderableVariables.map((v) => (
            <option key={v.name} value={v.name}>{v.name} ({v.type})</option>
          ))}
        </select>
        <div className="help">
          On every successful run, the bound variable advances per the strategy.
          On initialize, it resets to the initial-load value.
        </div>
      </div>

      {binding && variableType ? (
        <div style={{ display: "grid", gap: 8, paddingLeft: 2, borderLeft: "2px solid var(--border)" }}>
          <div className="field">
            <label className="label">Initial-load value</label>
            <input
              className="input"
              type={variableType === "int" || variableType === "decimal" ? "number" : variableType === "date" ? "date" : variableType === "datetime" ? "datetime-local" : "text"}
              value={binding.initial_load_value}
              onChange={(e) => onChange({ ...binding, initial_load_value: e.target.value })}
            />
            <div className="help">
              Low-water sentinel — substituted into the provider SQL during every <strong>initialize</strong> run
              so the predicate matches all rows.
            </div>
          </div>

          <div className="field">
            <label className="label">Advance strategy</label>
            <select
              className="select"
              value={binding.strategy.kind}
              onChange={(e) => handleStrategyKindChange(e.target.value as StrategyKind)}
            >
              {availableStrategies.includes("max_of_column") ? <option value="max_of_column">Max of column</option> : null}
              {availableStrategies.includes("last_run_time") ? <option value="last_run_time">Last successful run time</option> : null}
              {availableStrategies.includes("formula") ? <option value="formula">Formula (SQL expression)</option> : null}
            </select>
          </div>

          {binding.strategy.kind === "max_of_column" ? (
            <>
              <div className="field">
                <label className="label">Column</label>
                {upstreamColumns.length > 0 ? (
                  <select
                    className="select"
                    value={binding.strategy.column}
                    onChange={(e) => onChange({ ...binding, strategy: { ...binding.strategy as Extract<WatermarkStrategy, { kind: "max_of_column" }>, column: e.target.value } })}
                  >
                    <option value="">— pick a column —</option>
                    {upstreamColumns.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    value={binding.strategy.column}
                    onChange={(e) => onChange({ ...binding, strategy: { ...binding.strategy as Extract<WatermarkStrategy, { kind: "max_of_column" }>, column: e.target.value } })}
                    placeholder="column name (upstream schema not yet loaded)"
                  />
                )}
              </div>
              <BufferEditor binding={binding} onChange={onChange} />
            </>
          ) : null}

          {binding.strategy.kind === "last_run_time" ? (
            <BufferEditor binding={binding} onChange={onChange} />
          ) : null}

          {binding.strategy.kind === "formula" ? (
            <div className="field">
              <label className="label">Expression</label>
              <input
                className="input mono"
                value={binding.strategy.expression}
                onChange={(e) => onChange({ ...binding, strategy: { kind: "formula", expression: e.target.value } })}
                placeholder="e.g. current_date - INTERVAL 10 DAY"
              />
              <div className="help">
                Scalar SQL expression evaluated at advance time. Result type must match the bound variable type.
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function BufferEditor({
  binding,
  onChange,
}: {
  binding: WatermarkBinding;
  onChange: (next: WatermarkBinding) => void;
}) {
  // Buffer only meaningful for max_of_column / last_run_time strategies.
  if (binding.strategy.kind === "formula") return null;
  const s = binding.strategy;
  const bufferValueRaw = s.buffer_value ?? 0;
  const bufferUnit = s.buffer_unit ?? "hours";

  function updateBuffer(value: number, unit: BufferUnit) {
    if (value <= 0) {
      // Zero buffer → remove both fields (validator treats both-absent as no-buffer).
      const { buffer_value: _v, buffer_unit: _u, ...rest } = s;
      void _v; void _u;
      onChange({ ...binding, strategy: { ...rest } as WatermarkStrategy });
      return;
    }
    onChange({
      ...binding,
      strategy: { ...s, buffer_value: value, buffer_unit: unit } as WatermarkStrategy,
    });
  }

  return (
    <div className="field">
      <label className="label">Buffer (subtract from advance)</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="input"
          type="number"
          min={0}
          step={1}
          value={bufferValueRaw}
          onChange={(e) => updateBuffer(Number(e.target.value), bufferUnit)}
          style={{ maxWidth: 100 }}
        />
        <select
          className="select"
          value={bufferUnit}
          onChange={(e) => updateBuffer(bufferValueRaw, e.target.value as BufferUnit)}
          style={{ maxWidth: 120 }}
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
      </div>
      <div className="help">
        Subtracted from the computed advance to provide a safety margin against late-arriving rows.
        0 = no buffer.
      </div>
    </div>
  );
}
